import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  register: (userData: { email: string; password: string; firstName: string; lastName: string }) =>
    api.post('/auth/register', userData),
};

export const userAPI = {
  updateProfile: (profileData: { firstName: string; lastName: string; profilePicture?: string }) =>
    api.patch('/users/profile', profileData),
  buyCoins: (amount: number) =>
    api.post('/users/wallet/buy-coins', { amount }),
  getWalletBalance: () =>
    api.get('/users/wallet/balance'),
  getById: (id: string) =>
    api.get(`/users/${id}`),
  uploadProfilePicture: async (file: File) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    
    const response = await api.post('/users/profile/upload-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const gamesAPI = {
  create: (gameData: any) => api.post('/games', gameData),
  getAll: () => api.get('/games'),
  getPublic: () => api.get('/games/public'),
  getFriends: () => api.get('/games/friends'),
  getMy: () => api.get('/games/my'),
  getById: (id: string) => api.get(`/games/${id}`),
  getByInviteCode: (code: string) => api.get(`/games/invite/${code}`),
  join: (id: string, asSpectator: boolean = false) =>
    api.post(`/games/${id}/join`, { asSpectator }),
  sit: (id: string) => api.post(`/games/${id}/sit`),
  inviteFriend: (id: string, friendId: string) => api.post(`/games/${id}/invite`, { friendId }),
  leaveTable: (id: string) => api.post(`/games/${id}/leave-table`),
  leave: (id: string, resign = false) => api.post(`/games/${id}/leave`, { resign }),
  start: (id: string, cutIndex?: number) => api.post(`/games/${id}/start`, { cutIndex }),
  delete: (id: string) => api.delete(`/games/${id}`),
  getGameState: (id: string) => api.get(`/games/${id}/game-state`),
  startNextMatch: (id: string) => api.post(`/games/${id}/start-next-match`),
  restartGame: (id: string) => api.post(`/games/${id}/restart`),
};

export const friendsAPI = {
  getFriends: () => api.get('/friends'),
  sendRequest: (friendEmail: string) => api.post('/friends/request', { friendEmail }),
  acceptRequest: (friendId: string) => api.post('/friends/accept', { friendId }),
  rejectRequest: (friendId: string) => api.post('/friends/reject', { friendId }),
  removeFriend: (friendId: string) => api.delete(`/friends/${friendId}`),
  getPendingRequests: () => api.get('/friends/pending'),
  getReceivedRequests: () => api.get('/friends/received'),
  getSentRequests: () => api.get('/friends/sent'),
};

export const chatAPI = {
  sendMessage: (receiverId: string, message: string) => api.post('/chat/send', { receiverId, message }),
  getConversations: () => api.get('/chat/conversations'),
  getConversation: (friendId: string) => api.get(`/chat/conversation/${friendId}`),
  markAsRead: (senderId: string) => api.post(`/chat/read/${senderId}`),
  getUnreadCount: () => api.get('/chat/unread-count'),
  clearAll: () => api.delete('/chat/clear-all'),
};

export const analyticsAPI = {
  getUserStats: () => api.get('/analytics/stats'),
  getGameHistory: (params?: {
    page?: number;
    limit?: number;
    gameVariant?: string;
    gameMode?: string;
    result?: string;
  }) => api.get('/analytics/history', { params }),
  getLeaderboard: (limit?: number) => api.get('/analytics/leaderboard', { params: { limit } }),
  getCoinLeaderboard: (limit?: number) => api.get('/analytics/coin-leaderboard', { params: { limit } }),
  getRecentActivity: (limit?: number) => api.get('/analytics/recent-activity', { params: { limit } }),
  getGameVariantStats: () => api.get('/analytics/variant-stats'),
  recordGameResult: (gameData: any) => api.post('/analytics/record-game', gameData),
};

export const zbioAPI = {
  getBalance: () => api.get('/zbio/balance'),
  getMyWallet: () => api.get('/zbio/my-wallet'),
  getPlatformInfo: () => api.get('/zbio/platform-info'),
  createDeposit: (amount: number) => api.post('/zbio/deposit', { amount }),
  confirmDeposit: (depositId: string, paymentId: string) =>
    api.post('/zbio/deposit/confirm', { depositId, paymentId }),
  withdraw: (amount: number, recipientAddress: string) =>
    api.post('/zbio/withdraw', { amount, recipientAddress }),
};

export default api; 